import React, { useState, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Label,
  SpinnerLoader,
  Toastify,
  SingleSelect,
} from 'lift-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle } from 'lucide-react';
import { useUpdateConfiguration, useAllowableValues } from '../../hooks';
import type { ReportConfiguration } from '../../types';
import { transformToUI, transformToAPI } from '../../utils/valueTransformations';
import { isBreakdownReport as isBreakdownReportType } from '../../utils/reportTypeUtils';
import { shouldShowFiscalCalendar } from '../../utils/validations';
import { FISCAL_CALENDAR_WARNING, FISCAL_CALENDAR_DOCS_URL } from '../../constants';

interface EditConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  config: ReportConfiguration;
  brandId: number;
  brandName?: string;
  onSuccess: () => void;
  hasFiscalCalendar?: boolean;
}

const EditConfigDialog: React.FC<EditConfigDialogProps> = ({
  isOpen,
  onClose,
  config,
  brandId,
  brandName,
  onSuccess,
  hasFiscalCalendar = false,
}) => {
  const [useFiscalCalendar, setUseFiscalCalendar] = useState(config.use_fiscal_calendar);
  const [cadence, setCadence] = useState(transformToUI(config.cadence));
  const [showFiscalWarning, setShowFiscalWarning] = useState(false);

  const { data: allowableValues } = useAllowableValues();
  const { mutate: updateConfig, isPending } = useUpdateConfiguration();

  const isBreakdownReport = useMemo(() => {
    return isBreakdownReportType(config.report_type);
  }, [config.report_type]);

  const availableCadences = useMemo(() => {
    if (!allowableValues || isBreakdownReport) return [];
    
    const cadences = new Set<string>();
    const platformAPI = config.platform;
    const reportTypeAPI = config.report_type;
    
    allowableValues
      .filter((av) => {
        const avPlatformAPI = transformToAPI(av.platform);
        const avReportTypeAPI = transformToAPI(av.report_type);
        const matches = avPlatformAPI === platformAPI &&
                        avReportTypeAPI === reportTypeAPI &&
                        av.cadence;
        return matches;
      })
      .forEach((av) => {
        if (av.cadence) {
          cadences.add(av.cadence);
        }
      });
    
    const result = Array.from(cadences);
    return result;
  }, [allowableValues, config.platform, config.report_type, isBreakdownReport, cadence]);

  const showFiscalCalendarOption = useMemo(() => {
    return shouldShowFiscalCalendar(
      transformToUI(config.report_type),
      cadence
    );
  }, [config.report_type, cadence]);

  const handleCadenceChange = (value: string) => {
    setCadence(value);
    const cadenceAPI = transformToAPI(value);
    if (!cadenceAPI || !['prior_month', 'month_to_date'].includes(cadenceAPI)) {
      setUseFiscalCalendar(false);
    } else if (hasFiscalCalendar) {
      setUseFiscalCalendar(true);
    }
  };

  const handleFiscalCalendarChange = (checked: boolean | 'indeterminate') => {
    const enabled = checked === true;
    setUseFiscalCalendar(enabled);
    if (enabled && !hasFiscalCalendar) {
      setShowFiscalWarning(true);
    } else {
      setShowFiscalWarning(false);
    }
  };

  const handleSubmit = () => {
    const updates: Record<string, unknown> = {};
    
    if (!isBreakdownReport && cadence !== transformToUI(config.cadence)) {
      updates.cadence = transformToAPI(cadence);
    }
    
    if (useFiscalCalendar !== config.use_fiscal_calendar) {
      updates.use_fiscal_calendar = useFiscalCalendar;
    }

    if (Object.keys(updates).length === 0) {
      Toastify('info', 'No changes to save');
      onClose();
      return;
    }

    updateConfig(
      {
        id: config.id,
        brand_id: brandId,
        brand_name: brandName,
        data: updates,
        previous: {
          cadence: config.cadence,
          use_fiscal_calendar: config.use_fiscal_calendar,
        },
      },
      {
        onSuccess: () => {
          Toastify('success', 'Configuration updated successfully');
          onSuccess();
        },
        onError: (error: Error) => {
          Toastify('error', error.message || 'Failed to update configuration');
        },
      }
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Configuration</SheetTitle>
          <SheetDescription>
            Update the settings for this report configuration.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Read-only fields */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-gray-500">Platform</Label>
              <div className="p-2 bg-gray-50 rounded-md text-sm">
                {transformToUI(config.platform)}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-gray-500">Report Type</Label>
              <div className="p-2 bg-gray-50 rounded-md text-sm">
                {transformToUI(config.report_type)}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-gray-500">Granularity</Label>
              <div className="p-2 bg-gray-50 rounded-md text-sm">
                {transformToUI(config.granularity_level)}
              </div>
            </div>

            {isBreakdownReport && (
              <div className="space-y-1.5">
                <Label className="text-gray-500">Metric Type</Label>
                <div className="p-2 bg-gray-50 rounded-md text-sm">
                  {transformToUI(config.metric_type)}
                </div>
              </div>
            )}
          </div>

          {/* Editable fields */}
          {!isBreakdownReport && (
            <div className="space-y-1.5">
              <Label>Cadence</Label>
              <SingleSelect
                options={availableCadences.map((c) => ({ label: c, value: c }))}
                defaultValue={cadence || undefined}
                onChange={handleCadenceChange}
                placeholder="Select cadence..."
                containerClassName="w-full"
              />
            </div>
          )}

          {/* Fiscal Calendar Checkbox */}
          {showFiscalCalendarOption && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="fiscal-calendar-edit"
                  checked={useFiscalCalendar}
                  onCheckedChange={handleFiscalCalendarChange}
                />
                <Label htmlFor="fiscal-calendar-edit" className="cursor-pointer">
                  Include Fiscal Calendar Cadence
                </Label>
              </div>
              {showFiscalWarning && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-yellow-800">{FISCAL_CALENDAR_WARNING}</p>
                    <a 
                      href={FISCAL_CALENDAR_DOCS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-yellow-900 underline hover:text-yellow-700 mt-1 inline-block"
                    >
                      View documentation
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <>
                <SpinnerLoader className="h-4 w-4 mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default EditConfigDialog;
